import * as React from 'react'
import * as formik from 'formik'
import { styled } from 'next-yak'

import * as api from '@owl-nest/api-client/next'
import * as plume from '@ulule/owl-kit-components/next'
import * as model from '@owl-nest/models'
import { t } from '@owl-nest/localize'

import { useStep, getStepIndex, getStepProgress, Step } from '../useStep'
import { Progress } from '../components/Progress'
import { Coaching } from '../components/Coaching'
import { Actions } from '../components/Actions'
import { Tip } from '../components/Tip'
import { Form, SolidContent } from './styles'

type CommunityOnboardingProps = {
  project: model.project.ProjectForOwner
}

enum FormField {
  IMPORT_TYPE = 'import_type',
}

type Form = {
  [FormField.IMPORT_TYPE]: api.ImportType
}

export function CommunityOnboarding({ project }: CommunityOnboardingProps): React.ReactElement<any> {
  const { setStepValue } = useStep(project)

  return (
    <>
      <Progress
        progress={getStepProgress(Step.IMPORT_TYPE)}
        step={getStepIndex(Step.IMPORT_TYPE)}
        name={t('Ulule community')}
      />
      {project.community_members_count && (
        <Coaching project={project}>
          {t(
            `We've counted %(communityMembersCount)d people who have backed your previous Ulule projects. How would you like to appeal to this community?`,
            { communityMembersCount: project.community_members_count },
          )}
        </Coaching>
      )}
      <formik.Formik
        initialValues={{
          [FormField.IMPORT_TYPE]: api.ImportType.MEMBER,
        }}
        onSubmit={(values) => {
          setStepValue(undefined, values[FormField.IMPORT_TYPE])
        }}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {(formikBag) => {
          return (
            <Form onSubmit={formikBag.handleSubmit}>
              <SolidContent>
                <formik.Field name={FormField.IMPORT_TYPE}>
                  {({ field }: formik.FieldProps<api.ImportType>) => {
                    return (
                      <RadioGroupField type="big">
                        <plume.RadioField
                          name={field.name}
                          value={api.ImportType.MEMBER}
                          checked={field.value === api.ImportType.MEMBER}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          key={api.ImportType.MEMBER}
                          label={
                            <>
                              {t('Import these people as members of your page')} <Tag>{t('Recommended')}</Tag>
                            </>
                          }
                          description={t(
                            'Backers of your projects will immediately become free members when your page is launched and will receive a notification email.',
                          )}
                        />
                        <plume.RadioField
                          name={field.name}
                          value={api.ImportType.PREVIOUS_BACKER}
                          checked={field.value === api.ImportType.PREVIOUS_BACKER}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          key={api.ImportType.PREVIOUS_BACKER}
                          label={t('Notify all my former supporters of the launch of this page')}
                          description={t(
                            'Supporters of your projects will only receive a notification email when your page is launched and will have to subscribe to your page themselves.',
                          )}
                        />
                        <plume.RadioField
                          name={field.name}
                          value={api.ImportType.NONE}
                          checked={field.value === api.ImportType.NONE}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          key={api.ImportType.NONE}
                          label={t('Do nothing')}
                          description={t('You take full responsibility for communication.')}
                        />
                      </RadioGroupField>
                    )
                  }}
                </formik.Field>
              </SolidContent>

              <Actions>
                <Tip>{t('Nothing final, you can edit this information later.')}</Tip>
                <plume.Button kind="primary">{t('Next')}</plume.Button>
              </Actions>
            </Form>
          )
        }}
      </formik.Formik>
    </>
  )
}

const RadioGroupField = styled(plume.RadioGroupField)`
  max-width: 593px;
`

const Tag = styled(plume.Tag)`
  display: inline-block;
`
